import React, { useEffect, useMemo, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import PropTypes from 'prop-types'
import { UserIcon, UsersIcon, AwardIcon } from 'lucide-react'

import { Button } from '../../ui/button'
import { Input } from '../../ui/input'
import { Label } from '../../ui/label'
import { Checkbox } from '../../ui/checkbox'
import { RadioGroup, RadioGroupItem } from '../../ui/radio-group'
import AddressInput from '../../leevo_ui/AddressInput/AddressInput'
import { DatePickerBirthdate } from '../../ui/date-picker-birthdate'
import SkillLevelSelect from '../../leevo_ui/SkillLevelSelect/SkillLevelSelect'
import UploadButton from '../../leevo_ui/UploadButton/UploadButton'
import { isValidAddress } from '../../../utilities/validators'
import { requestCreator } from '../../../utilities/requests'
import GuardianManager from './GuardianManager/GuardianManager'
import useStudentNameSearch from '../../../utilities/hooks/useStudentNameSearch'
import StudentDuplicateWarning from '../StudentDuplicateWarning/StudentDuplicateWarning'

const { put: updateStudent, cancel: cancelStudentRequests } = requestCreator()

function StudentEditForm({
  id,
  facilityId,
  onSubmit,
  fullName = '',
  preferredName = '',
  cloudinaryPhotoPublicId,
  birthdate = '',
  gender = 'unspecified',
  ltsNumber = '',
  skillLevel = '',
  address = {},
  guardians,
  skateRental = {},
  requiresAddress = false,
  requiresBirthdate = false,
  requiresGender = false,
  requiresLtsNumber = false,
  facilitySupportsSkating,
  facilityProvidesFreeRentalSkates,
  onSelectExistingStudent = null
}) {
  useEffect(() => {
    return cancelStudentRequests
  }, [])

  const [selectedStudent, setSelectedStudent] = useState(null)
  const { matches, searchByName, clearMatches } = useStudentNameSearch(facilityId)

  const defaultValues = useMemo(
    () => ({
      fullName,
      preferredName,
      cloudinaryPhotoPublicId,
      gender,
      birthdate,
      ltsNumber,
      skillLevel,
      address,
      skateRental: {
        active: skateRental?.active || false,
        skateType: skateRental?.skateType || '',
        size: skateRental?.size || ''
      },
      guardians: guardians || []
    }),
    [
      fullName,
      preferredName,
      cloudinaryPhotoPublicId,
      gender,
      birthdate,
      ltsNumber,
      skillLevel,
      address,
      skateRental,
      guardians
    ]
  )

  const {
    handleSubmit,
    control,
    watch,
    register,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm({
    defaultValues
  })

  const watchSkateRentalActive = watch('skateRental.active')

  const handleFormSubmit = (values) => {
    if (selectedStudent && onSelectExistingStudent) {
      onSelectExistingStudent(selectedStudent)
      return
    }
    onSubmit({ ...values, guardians: !id ? values.guardians : null })
  }

  const { onBlur: rhfFullNameBlur, onChange: rhfFullNameChange, ...restFullName } = register('fullName', {
    required: 'Full name is required'
  })

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="min-h-screen bg-gray-100 pb-10">
      <div className="px-4 py-6 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">{id ? 'Edit Student' : 'New Student'}</h1>
          <p className="mt-2 text-sm text-gray-600">
            Complete the student profile, guardian information, and skating details
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="bg-white rounded-lg shadow lg:col-span-2">
            <div className="flex items-center px-6 py-4 border-b border-gray-200">
              <UserIcon className="w-5 h-5 mr-2 text-purple-600" />
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Student Profile</h2>
                <p className="text-sm text-gray-600">
                  This information is displayed in your dashboard, and to coaches and directors
                </p>
              </div>
            </div>

            <div className="px-6 py-4">
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <div>
                  <Label htmlFor="full_name">Full Name *</Label>
                  <Input
                    id="full_name"
                    type="text"
                    placeholder="Richard Button"
                    autoFocus
                    {...restFullName}
                    onBlur={(e) => {
                      rhfFullNameBlur(e)
                      if (!id && !selectedStudent) searchByName(e.target.value)
                    }}
                    onChange={(e) => {
                      rhfFullNameChange(e)
                      if (selectedStudent) {
                        setSelectedStudent(null)
                        clearMatches()
                      }
                    }}
                    className="mt-2"
                  />
                  {errors.fullName && <p className="mt-1 text-xs text-red-500">{errors.fullName.message}</p>}
                  {selectedStudent ? (
                    <div className="mt-2 rounded-lg border border-amber-400 bg-amber-50 px-3 py-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-semibold text-amber-800">
                            ✓ Using existing student: {selectedStudent.full_name}
                          </p>
                          <p className="text-xs text-amber-700">Review their info and make any needed updates below.</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedStudent(null)
                            clearMatches()
                            setValue('fullName', '')
                          }}
                          className="ml-3 text-xs text-amber-700 underline hover:text-amber-900"
                        >
                          Start over
                        </button>
                      </div>
                    </div>
                  ) : (
                    matches.length > 0 && (
                      <StudentDuplicateWarning
                        matches={matches}
                        onSelectStudent={(student) => {
                          setSelectedStudent(student)
                          clearMatches()
                          setValue('fullName', student.full_name)
                          if (student.birthdate) setValue('birthdate', student.birthdate)
                          if (student.skill_level_id) setValue('skillLevel', String(student.skill_level_id))
                        }}
                        onDismiss={clearMatches}
                      />
                    )
                  )}
                </div>

                <div>
                  <Label htmlFor="preferred_name">Preferred Name *</Label>
                  <Input
                    id="preferred_name"
                    type="text"
                    placeholder="Richard"
                    {...register('preferredName', { required: 'Preferred name is required' })}
                    className="mt-2"
                  />
                  {errors.preferredName && <p className="mt-1 text-xs text-red-500">{errors.preferredName.message}</p>}
                </div>

                {(!requiresLtsNumber || !facilitySupportsSkating) && (
                  <div>
                    <Controller
                      name="skillLevel"
                      control={control}
                      rules={{ required: 'Skill level is required' }}
                      render={({ field, fieldState: { error } }) => (
                        <SkillLevelSelect {...field} name="the student" helpText="" error={error?.message} />
                      )}
                    />
                  </div>
                )}

                <div>
                  <Controller
                    name="cloudinaryPhotoPublicId"
                    control={control}
                    render={({ field }) => (
                      <UploadButton
                        cloudinaryPhotoPublicId={field.value}
                        onPhotoUpload={(newPublicId) => {
                          if (!id) {
                            field.onChange(newPublicId)
                          } else {
                            updateStudent(`/api/facilities/${facilityId}/users/${id}`, {
                              user: { cloudinaryPhotoPublicId: newPublicId }
                            }).then(() => {
                              field.onChange(newPublicId)
                            })
                          }
                        }}
                      />
                    )}
                  />
                </div>

                {requiresGender && (
                  <div className="lg:col-span-2">
                    <Controller
                      name="gender"
                      control={control}
                      rules={{ required: 'Gender is required' }}
                      render={({ field, fieldState: { error } }) => (
                        <div>
                          <Label htmlFor="gender" className="mb-3 block">
                            Gender
                          </Label>
                          <RadioGroup
                            value={field.value}
                            onValueChange={field.onChange}
                            className="grid grid-cols-2 gap-4"
                          >
                            {[
                              { label: 'Female', value: 'female' },
                              { label: 'Male', value: 'male' },
                              { label: 'Non-binary', value: 'non_binary' },
                              { label: 'Unspecified', value: 'unspecified' }
                            ].map((option) => (
                              <div key={option.value} className="flex items-center space-x-2">
                                <RadioGroupItem value={option.value} id={`gender-${option.value}`} />
                                <Label htmlFor={`gender-${option.value}`} className="font-normal cursor-pointer">
                                  {option.label}
                                </Label>
                              </div>
                            ))}
                          </RadioGroup>
                          {error && <p className="mt-2 text-xs text-red-500">{error.message}</p>}
                        </div>
                      )}
                    />
                  </div>
                )}

                {requiresBirthdate && (
                  <div>
                    <Label htmlFor="birthdate">Birthdate</Label>
                    <Controller
                      name="birthdate"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <DatePickerBirthdate
                          value={field.value}
                          onChange={field.onChange}
                          error={error?.message}
                          className="mt-2"
                        />
                      )}
                    />
                  </div>
                )}

                {requiresAddress && (
                  <div className="lg:col-span-2">
                    <Controller
                      name="address"
                      control={control}
                      rules={{
                        validate: (value) => {
                          const result = isValidAddress(value)
                          return result === undefined ? true : result
                        }
                      }}
                      render={({ field, fieldState: { error } }) => (
                        <AddressInput {...field} label="Mailing address" errorMessage={error?.message || ''} />
                      )}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow lg:col-span-2">
            <div className="flex items-center px-6 py-4 border-b border-gray-200">
              <UsersIcon className="w-5 h-5 mr-2 text-purple-600" />
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Guardian Information</h2>
                <p className="text-sm text-gray-600">
                  Information related to the student&apos;s parent, guardian or other responsible party
                </p>
              </div>
            </div>

            <div className="px-6 py-4">
              <Controller
                name="guardians"
                control={control}
                render={({ field }) => (
                  <GuardianManager onChange={field.onChange} guardians={guardians} studentId={id} />
                )}
              />
            </div>
          </div>

          {facilitySupportsSkating && (
            <div className="bg-white rounded-lg shadow lg:col-span-2">
              <div className="flex items-center px-6 py-4 border-b border-gray-200">
                <AwardIcon className="w-5 h-5 mr-2 text-purple-600" />
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Skating Information</h2>
                  <p className="text-sm text-gray-600">
                    Information related to skating and the student&apos;s standing with Learn to Skate USA
                  </p>
                </div>
              </div>

              <div className="px-6 py-4">
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  {facilityProvidesFreeRentalSkates && (
                    <div className="lg:col-span-2">
                      <div className="flex items-center space-x-2">
                        <Controller
                          name="skateRental.active"
                          control={control}
                          render={({ field }) => (
                            <Checkbox id="skateRental.active" checked={field.value} onCheckedChange={field.onChange} />
                          )}
                        />
                        <Label htmlFor="skateRental.active" className="cursor-pointer">
                          Needs skate rental?
                        </Label>
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        The facility {watchSkateRentalActive ? 'will' : "won't"} have skates ready for the student
                        before class
                      </p>

                      {watchSkateRentalActive && (
                        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
                          <Controller
                            name="skateRental.skateType"
                            control={control}
                            rules={{
                              validate: (value) => {
                                if (watchSkateRentalActive && !value) {
                                  return 'Skate type is required when rental is active'
                                }
                                return true
                              }
                            }}
                            render={({ field, fieldState: { error } }) => (
                              <div>
                                <Label htmlFor="skateType">What type of skate?</Label>
                                <select
                                  {...field}
                                  id="skateType"
                                  className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm mt-2"
                                >
                                  <option value="">Select a type</option>
                                  <option value="youth_figure">Youth Figure</option>
                                  <option value="men_figure">Men&apos;s Figure</option>
                                  <option value="women_figure">Women&apos;s Figure</option>
                                  <option value="men_hockey">Men&apos;s Hockey</option>
                                  <option value="women_hockey">Women&apos;s Hockey</option>
                                  <option value="youth_hockey">Youth Hockey</option>
                                </select>
                                {error && <p className="mt-1 text-xs text-red-500">{error.message}</p>}
                              </div>
                            )}
                          />

                          <Controller
                            name="skateRental.size"
                            control={control}
                            rules={{
                              validate: (value) => {
                                if (watchSkateRentalActive && !value) {
                                  return 'Skate size is required when rental is active'
                                }
                                return true
                              }
                            }}
                            render={({ field, fieldState: { error } }) => (
                              <div>
                                <Label htmlFor="skateSize">What size skate?</Label>
                                <Input
                                  {...field}
                                  id="skateSize"
                                  type="number"
                                  step="0.5"
                                  max="20"
                                  min="1"
                                  className="mt-2"
                                />
                                <p className="mt-1 text-xs text-gray-500">You can specify half sizes. e.g. 10.5</p>
                                {error && <p className="mt-1 text-xs text-red-500">{error.message}</p>}
                              </div>
                            )}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {requiresLtsNumber && (
                    <div>
                      <Label htmlFor="ltsNumber">LTS Member Number</Label>
                      <Input
                        id="ltsNumber"
                        type="text"
                        placeholder="LTS or USFSA number"
                        {...register('ltsNumber')}
                        className="mt-2"
                      />
                    </div>
                  )}

                  <div>
                    <Controller
                      name="skillLevel"
                      control={control}
                      rules={{ required: 'Skill level is required' }}
                      render={({ field, fieldState: { error } }) => (
                        <SkillLevelSelect {...field} name="the student" helpText="" error={error?.message} />
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end">
          <Button type="submit" disabled={isSubmitting} variant="default" size="lg">
            {isSubmitting
              ? 'Saving...'
              : selectedStudent
              ? `Continue with ${selectedStudent.full_name} →`
              : id
              ? 'Save Changes'
              : 'Create Student'}
          </Button>
        </div>
      </div>
    </form>
  )
}

// FIXME: Should pull this from rails back end in some way.
StudentEditForm.genders = ['male', 'female', 'non_binary', 'unspecified']

StudentEditForm.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  facilityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fullName: PropTypes.string,
  preferredName: PropTypes.string,
  cloudinaryPhotoPublicId: PropTypes.string,
  email: PropTypes.string,
  birthdate: PropTypes.string,
  guardians: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      fullName: PropTypes.string,
      phone: PropTypes.string,
      email: PropTypes.string,
      cloudinaryPhotoPublicId: PropTypes.string,
      pending: PropTypes.bool,
      emailBounced: PropTypes.bool
    })
  ),
  gender: PropTypes.oneOf(StudentEditForm.genders),
  ltsNumber: PropTypes.string,
  skillLevel: PropTypes.string,
  address: PropTypes.shape({
    line1: PropTypes.string,
    line2: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    postCode: PropTypes.string
  }),
  skateRental: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onSelectExistingStudent: PropTypes.func,
  requiresAddress: PropTypes.bool,
  requiresBirthdate: PropTypes.bool,
  requiresGender: PropTypes.bool,
  requiresLtsNumber: PropTypes.bool,
  facilitySupportsSkating: PropTypes.bool,
  facilityProvidesFreeRentalSkates: PropTypes.bool
}

export default StudentEditForm
